import React, { useState } from 'react';
import { GastoPlan, PagoPlan, Plan } from '../../types';
import { useAMedias } from '../../context/AMediasContext';
import { useAuth } from '../../context/AuthContext';
import { useConfirmar } from '../../context/ConfirmarContext';
import { participanteConMovimientos } from '../../utils/amediasUtils';
import {
  Check, Copy, Crown, Edit2, Link2, Link2Off, LogOut, Plus, RefreshCw, Share2, Trash2,
  UserCheck, UserMinus, UserX, X,
} from 'lucide-react';
import { claseInput, MensajeError, ModalBase } from './ui';
import { NOMBRE_APP, URL_PUBLICA } from '../../config/marca';
import { copiarTexto, compartirTexto, puedeCompartir } from '../../utils/plataforma';

interface Props {
  plan: Plan;
  gastos: GastoPlan[];
  pagos: PagoPlan[];
  onClose: () => void;
  /** Tras salir del plan (ya no se puede ver). */
  onSalir: () => void;
}

// URL_PUBLICA y no window.location: dentro de la app de iOS el origen es capacitor://localhost
export const enlaceInvitacion = (token: string) => `${URL_PUBLICA}/amedias/unirse/${token}`;

/**
 * Quién participa en el plan y con qué rol.
 *
 * - Organizador: gestiona participantes, la invitación y los roles.
 * - Miembro: ve la lista y puede salir del plan.
 *
 * Quien ya tiene gastos o pagos no se puede borrar (descuadraría los saldos):
 * se desactiva. Quien sale del plan deja de verlo, pero su historial se queda.
 */
export const ModalParticipantes: React.FC<Props> = ({ plan, gastos, pagos, onClose, onSalir }) => {
  const {
    anadirParticipante, actualizarParticipante, eliminarParticipante,
    abrirInvitacion, cerrarInvitacion, cambiarOrganizador, quitarMiembro, salirDelPlan,
  } = useAMedias();
  const { user } = useAuth();
  const confirmar = useConfirmar();

  const [nuevo, setNuevo] = useState('');
  const [editando, setEditando] = useState<{ id: string; nombre: string } | null>(null);
  const [error, setError] = useState('');
  const [copiado, setCopiado] = useState(false);
  const [ocupado, setOcupado] = useState(false);

  const uid = user?.uid ?? '';
  const esOrganizador = plan.organizadoresUids.includes(uid);
  const soyUnicoOrganizador = esOrganizador && plan.organizadoresUids.length === 1;
  const esMiembro = (u?: string) => !!u && plan.miembrosUids.includes(u);

  const existe = (nombre: string, salvo?: string) => plan.participantes.some(p =>
    p.id !== salvo && p.nombre.trim().toLowerCase() === nombre.trim().toLowerCase());

  const ejecutar = async (fn: () => Promise<unknown>) => {
    setError('');
    setOcupado(true);
    try { await fn(); } catch (err) {
      console.error(err);
      setError('No se pudo guardar. Inténtalo de nuevo.');
    } finally { setOcupado(false); }
  };

  const anadir = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nuevo.trim()) return;
    if (existe(nuevo)) { setError(`Ya hay alguien llamado «${nuevo.trim()}».`); return; }
    await ejecutar(async () => { await anadirParticipante(plan.id, nuevo); setNuevo(''); });
  };

  const guardarNombre = async () => {
    if (!editando) return;
    if (!editando.nombre.trim()) { setError('El nombre no puede quedar vacío.'); return; }
    if (existe(editando.nombre, editando.id)) { setError(`Ya hay alguien llamado «${editando.nombre.trim()}».`); return; }
    await ejecutar(async () => {
      await actualizarParticipante(plan.id, editando.id, { nombre: editando.nombre });
      setEditando(null);
    });
  };

  const copiar = async () => {
    if (!plan.invitacionToken) return;
    try {
      await copiarTexto(enlaceInvitacion(plan.invitacionToken));
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      setError('No se pudo copiar. Mantén pulsado el enlace para copiarlo a mano.');
    }
  };

  const compartir = async () => {
    if (!plan.invitacionToken) return;
    try {
      await compartirTexto({
        titulo: plan.nombre,
        texto: `Únete a «${plan.nombre}» en ${NOMBRE_APP} para llevar los gastos a medias`,
        url: enlaceInvitacion(plan.invitacionToken),
      });
    } catch {
      setError('No se pudo abrir el menú de compartir. Copia el enlace y pégalo donde quieras.');
    }
  };

  const salir = async () => {
    if (!(await confirmar({
      titulo: `¿Salir de «${plan.nombre}»?`,
      mensaje: 'Dejarás de verlo; tus gastos se quedan en el plan.',
      accion: 'Salir',
    }))) return;
    await ejecutar(async () => {
      // Un organizador que sale lo hace por la vía de organizador (y deja de serlo)
      if (esOrganizador) await quitarMiembro(plan, uid);
      else await salirDelPlan(plan);
      onSalir();
    });
  };

  const botonIcono = 'p-2 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition disabled:opacity-40';

  return (
    <ModalBase titulo="Participantes" onClose={onClose}>
      <div className="p-5 space-y-5">
        <MensajeError texto={error} />

        {/* ── Invitación ───────────────────────────────────────────────── */}
        {esOrganizador && (
          <section className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-900/20 p-3 space-y-2">
            <p className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
              <Link2 className="w-4 h-4 text-emerald-600" /> Invitar con un enlace
            </p>
            {plan.invitacionToken ? (
              <>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Quien lo abra podrá unirse, ver el plan y apuntar sus gastos. Si no usa la app, se creará una cuenta
                  y se le activará A medias.
                </p>
                <div className="flex gap-2">
                  <input readOnly value={enlaceInvitacion(plan.invitacionToken)} onFocus={e => e.target.select()}
                    className={`${claseInput} text-xs`} aria-label="Enlace de invitación" />
                  <button onClick={copiar} title="Copiar enlace"
                    className="shrink-0 inline-flex items-center gap-1 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold">
                    {copiado ? <><Check className="w-4 h-4" /> Copiado</> : <><Copy className="w-4 h-4" /> Copiar</>}
                  </button>
                  {puedeCompartir() && (
                    <button onClick={compartir} title="Compartir" className={botonIcono}><Share2 className="w-4 h-4" /></button>
                  )}
                </div>
                <div className="flex flex-wrap gap-3 text-xs">
                  <button disabled={ocupado} onClick={() => ejecutar(() => abrirInvitacion(plan))}
                    className="inline-flex items-center gap-1 font-semibold text-gray-600 dark:text-gray-400 hover:underline"
                    title="El enlace actual dejará de funcionar">
                    <RefreshCw className="w-3.5 h-3.5" /> Cambiar enlace
                  </button>
                  <button disabled={ocupado} onClick={() => ejecutar(() => cerrarInvitacion(plan))}
                    className="inline-flex items-center gap-1 font-semibold text-red-600 dark:text-red-400 hover:underline">
                    <Link2Off className="w-3.5 h-3.5" /> Cerrar invitación
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Crea un enlace y pásalo por WhatsApp o como quieras. Podrás cerrarlo cuando estéis todos.
                </p>
                <button disabled={ocupado} onClick={() => ejecutar(() => abrirInvitacion(plan))}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700
                             text-white text-sm font-semibold transition disabled:opacity-60">
                  <Link2 className="w-4 h-4" /> Crear enlace de invitación
                </button>
              </>
            )}
          </section>
        )}

        {/* ── Lista ────────────────────────────────────────────────────── */}
        <ul className="divide-y divide-gray-100 dark:divide-gray-700 rounded-xl border border-gray-200 dark:border-gray-700">
          {plan.participantes.map(p => {
            const conMovimientos = participanteConMovimientos(p.id, gastos, pagos);
            const esYo = !!p.uid && p.uid === uid;
            const miembro = esMiembro(p.uid);
            const organiza = miembro && plan.organizadoresUids.includes(p.uid!);
            const estado = !p.uid ? 'Solo nombre' : miembro ? (organiza ? 'Organizador' : 'Miembro') : 'Ya no está en el plan';
            return (
              <li key={p.id} className={`flex items-center gap-1 px-3 py-2 ${p.activo ? '' : 'opacity-60'}`}>
                {editando?.id === p.id ? (
                  <>
                    <input type="text" value={editando.nombre} autoFocus
                      onChange={e => setEditando({ id: p.id, nombre: e.target.value })}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); guardarNombre(); }
                        if (e.key === 'Escape') setEditando(null);
                      }}
                      className={claseInput} />
                    <button onClick={guardarNombre} title="Guardar" className={botonIcono}><Check className="w-4 h-4" /></button>
                    <button onClick={() => setEditando(null)} title="Cancelar" className={botonIcono}><X className="w-4 h-4" /></button>
                  </>
                ) : (
                  <>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                        {p.nombre}{esYo && <span className="font-normal text-gray-500"> (tú)</span>}
                      </p>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        {organiza && <Crown className="w-3 h-3 text-amber-500" />}
                        {estado}{!p.activo && ' · Desactivado'}
                      </p>
                    </div>

                    {esOrganizador && (
                      <>
                        {/* Roles: solo para quien es miembro, y nunca dejar el plan sin organizador */}
                        {miembro && !esYo && (
                          <button disabled={ocupado}
                            onClick={() => ejecutar(() => cambiarOrganizador(plan, p.uid!, !organiza))}
                            title={organiza ? 'Dejar de ser organizador' : 'Hacer organizador: podrá tocar los gastos de todos'}
                            className={`${botonIcono} ${organiza ? '!text-amber-500' : ''}`}>
                            <Crown className="w-4 h-4" />
                          </button>
                        )}
                        {esYo && organiza && !soyUnicoOrganizador && (
                          <button disabled={ocupado} onClick={() => ejecutar(() => cambiarOrganizador(plan, uid, false))}
                            title="Dejar de ser organizador" className={`${botonIcono} !text-amber-500`}>
                            <Crown className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => setEditando({ id: p.id, nombre: p.nombre })} title="Cambiar nombre"
                          className={botonIcono}><Edit2 className="w-4 h-4" /></button>
                        {miembro && !esYo && (
                          <button disabled={ocupado}
                            onClick={async () => (await confirmar({
                                titulo: `¿Quitar a ${p.nombre} del plan?`,
                                mensaje: 'Dejará de verlo; sus gastos se quedan.',
                                accion: 'Quitar',
                              })) && ejecutar(() => quitarMiembro(plan, p.uid!))}
                            title="Quitar del plan" className={botonIcono}><UserMinus className="w-4 h-4" /></button>
                        )}
                        {!esYo && !miembro && (conMovimientos ? (
                          <button disabled={ocupado}
                            onClick={() => ejecutar(() => actualizarParticipante(plan.id, p.id, { activo: !p.activo }))}
                            title={p.activo
                              ? 'Desactivar: no saldrá al apuntar gastos nuevos, pero conserva lo que ya tiene'
                              : 'Volver a activar'}
                            className={botonIcono}>
                            {p.activo ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                          </button>
                        ) : (
                          <button disabled={ocupado}
                            onClick={async () => (await confirmar({ titulo: `¿Quitar a ${p.nombre} del plan?`, accion: 'Quitar' }))
                              && ejecutar(() => eliminarParticipante(plan.id, p.id))}
                            title="Quitar del plan"
                            className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        ))}
                      </>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ul>

        {esOrganizador && (
          <form onSubmit={anadir} className="flex gap-2">
            <input type="text" value={nuevo} onChange={e => setNuevo(e.target.value)}
              placeholder="Añadir a alguien solo por su nombre" className={claseInput} />
            <button type="submit" disabled={!nuevo.trim() || ocupado}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700
                         text-white text-sm font-semibold transition disabled:opacity-50">
              <Plus className="w-4 h-4" /> Añadir
            </button>
          </form>
        )}

        <div className="flex items-center justify-between gap-3 pt-1">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {esOrganizador
              ? 'La corona nombra organizadores: pueden tocar los gastos de todos y gestionar el plan.'
              : 'Los organizadores gestionan el plan; tú puedes tocar los gastos y pagos que apuntes.'}
          </p>
          {esMiembro(uid) && !soyUnicoOrganizador && (
            <button onClick={salir} disabled={ocupado}
              className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-red-600 dark:text-red-400 hover:underline">
              <LogOut className="w-3.5 h-3.5" /> Salir del plan
            </button>
          )}
        </div>
      </div>
    </ModalBase>
  );
};
